import {useApolloClient, ApolloClient, gql} from "@apollo/client";
import {
    Error,
    ExtensionProfileRuleAction,
    UserProfileRuleData,
    UserProfileRuleMatchType,
    UserActivationSettings,
    MutationSaveUserProfileRuleArgs,
    UpdateUserProfileData,
} from "@/api";
import DataSource from "./DataSource";
import {ErrorWithContext} from "@/utils/ErrorHelper";
import {useState} from "react";
import {createInitialData, createInitialRuleData} from "./initialData";
import {HookClass} from "@dvrt/graphql-widgets";
import {getDiff} from "recursive-diff";
import {createRollbackOnErrorContext} from "@/apollo-client/factory";
import deepEqual from "deep-equal";
import {useDataSource} from "./tools";

type Data = {
    profile: UpdateUserProfileData;
    rules: MutationSaveUserProfileRuleArgs[];
};

type MutationDefinitionData = {
    statement: string;
    variables?: Record<
        string,
        {
            value: any;
            type: string;
        }
    >;
};

type Mutation = {
    statement: string;
    variables: Record<string, any>;
};

type ProfileErrors = {[k in keyof UpdateUserProfileData]: ErrorWithContext[]};
type RuleErrors = {[k in number]: Error[]};

export default class UpdateForm extends HookClass {
    private id!: number;
    dataSource!: DataSource;
    values!: Data;
    private setValues!: (data: Data) => void;
    protected client!: ApolloClient<object>;
    private profileErrors!: ProfileErrors;
    private setProfileErrors!: (e: ProfileErrors) => void;
    private ruleErrors!: RuleErrors;
    private setRuleErrors!: (e: RuleErrors) => void;

    useHooks(id: number) {
        this.id = id;
        this.dataSource = useDataSource();
        [this.values, this.setValues] = useState(this.initialData);
        [this.profileErrors, this.setProfileErrors] = useState({});
        [this.ruleErrors, this.setRuleErrors] = useState({});
        this.client = useApolloClient();
    }

    getProfileErrors(field: keyof UpdateUserProfileData): ErrorWithContext[] {
        return this.profileErrors[field] ?? [];
    }

    getRuleErrors(idx: number): Error[] {
        return this.ruleErrors[idx] ?? [];
    }

    get initialData(): Data {
        const profile = this.dataSource.getProfile(this.id);
        const profileData = createInitialData(profile);

        return {
            profile: profileData,
            rules: createInitialRuleData(profile),
        };
    }

    generateProfileMutation(initialData: Data): MutationDefinitionData {
        const profileMods = getDiff(initialData.profile, this.values.profile);

        if (!profileMods.length) return null;

        const data: UpdateUserProfileData = {};

        for (const mod of profileMods) {
            if (mod.path[0] == "settings") {
                data[mod.path[0]] = {};
                if (this.values.profile.settings.activationTimes.length) {
                    data[mod.path[0]].activationTimes = this.values.profile.settings.activationTimes;
                }

                if (this.values.profile.settings.specialDays.length) {
                    data[mod.path[0]].specialDays = this.values.profile.settings.specialDays;
                } else {
                    data[mod.path[0]].specialDays = [];
                }
            } else {
                (data[mod.path[0] as keyof UpdateUserProfileData] as any) = mod.val;
            }
        }

        return {
            variables: {
                profileData: {
                    value: data,
                    type: "UpdateUserProfileData!",
                },
            },
            statement: `updateUserProfile(id: ${this.id} data: $profileData) {errors {context, location, type}}`,
        };
    }

    generateRuleMutations(initialData: Data): MutationDefinitionData[] {
        const res: MutationDefinitionData[] = [];

        const updateRules: {[idx: number]: UserProfileRuleData} = {};
        const deleteRuleIds: number[] = [];
        const createRules: {[idx: number]: UserProfileRuleData} = {};

        for (const [idx, rule] of this.values.rules.entries()) {
            if (rule.id) {
                const initialRule = initialData.rules.filter((r) => r.id === rule.id)[0];
                const diff = getDiff(initialRule.data, rule.data);
                if (diff.length) {
                    const data: UserProfileRuleData = {};
                    for (const item of diff) (data[item.path[0] as keyof UserProfileRuleData] as any) = item.val;
                    updateRules[idx] = data;
                }
            } else {
                createRules[idx] = structuredClone(rule.data);
            }
        }

        for (const rule of initialData.rules) {
            const formRules = this.values.rules.filter((r) => r.id === rule.id);
            if (!formRules.length) deleteRuleIds.push(rule.id);
        }

        for (const [idx, data] of Object.entries(updateRules)) {
            const id = this.values.rules[Number(idx)].id;

            const varName = `updateRuleData${id}`;
            res.push({
                variables: {
                    [varName]: {
                        value: data,
                        type: "UserProfileRuleData!",
                    },
                },
                statement: `updateRule${idx}: saveUserProfileRule(id: ${id} data: $${varName}) {id errors {location type context}}`,
            });
        }

        for (const id of deleteRuleIds) {
            res.push({
                statement: `deleteRule${id}: deleteUserProfileRule(id: ${id}) {type}`,
            });
        }

        for (const [idx, data] of Object.entries(createRules)) {
            const varName = `createRuleData${idx}`;
            res.push({
                variables: {
                    [varName]: {
                        value: data,
                        type: "UserProfileRuleData!",
                    },
                },
                statement: `createRule${idx}: saveUserProfileRule(data: $${varName}) {id errors {location type context}}`,
            });
        }

        return res;
    }

    private generateMutation(): Mutation {
        const initialData = this.initialData;
        const profileMutation = this.generateProfileMutation(initialData);

        const variableDefinitions: string[] = [];
        const variables: Record<string, any> = {};
        const statement: string[] = [];
        let definitionString = "";

        const mutations: MutationDefinitionData[] = [];

        if (profileMutation) mutations.push(profileMutation);

        mutations.push(...this.generateRuleMutations(initialData));

        console.debug("generateMutation mutations:", mutations);

        for (const mutation of mutations) {
            if (mutation.variables) {
                for (const [name, desc] of Object.entries(mutation.variables)) {
                    variableDefinitions.push(`$${name}: ${desc.type}`);
                    variables[name] = desc.value;
                }
            }
            statement.push(mutation.statement);
        }

        if (variableDefinitions.length) definitionString = `(${variableDefinitions.join(" ")})`;

        return {
            statement: `mutation UpdateExtensionProfile${definitionString} { ${statement.join("\n")} }`,
            variables,
        };
    }

    private handleCreatedRules(data: any) {
        const createRuleIds: [number, number][] = [];
        for (const [idx, rule] of this.values.rules.entries()) {
            if (!rule.id) {
                const createResult = data[`createRule${idx}`];
                const ruleId = createResult.id;

                if (ruleId) createRuleIds.push([idx, ruleId]);
            }
        }

        if (!createRuleIds.length) return;

        this.updateValues((d) => {
            for (const [idx, id] of createRuleIds) {
                d.rules[idx].id = id;
                delete d.rules[idx].data.userProfileId;
            }
        });
    }

    // Remove the whitespaces from the beginning and end of the numbers
    removeStartAndEndWhitespaceFromValues() {
        for (const field of Object.keys(this.values.profile) as (keyof UpdateUserProfileData)[]) {
            // All the fields that end with "Number" should be trimmed
            if (field.endsWith("Number"))
                (this.values.profile[field] as string) = (this.values.profile[field] as string).trim();
        }
    }

    async submit(): Promise<Error[]> {
        this.removeStartAndEndWhitespaceFromValues();
        const mutationData = this.generateMutation();
        const mutation = gql(mutationData.statement);
        const res = await this.client.mutate({
            mutation,
            variables: mutationData.variables,
            context: createRollbackOnErrorContext(),
        });

        this.handleCreatedRules(res.data);

        return this.handleProfileErrors(res.data).concat(this.handleRuleErrors(res.data));
    }

    private handleRuleErrors(data: any): Error[] {
        const errors: RuleErrors = {};
        const backendErrors: Error[] = [];
        for (const [field, response] of Object.entries(data)) {
            const ruleErrors = (response as any).errors;
            if (!ruleErrors) continue;

            const ruleId = Number(field.replace("updateRule", "").replace("createRule", "").replace("deleteRule", ""));

            if (!errors[ruleId]) errors[ruleId] = [];
            errors[ruleId].push(...ruleErrors);
            backendErrors.push(...ruleErrors);
        }

        this.setRuleErrors(errors);
        return backendErrors;
    }

    private handleProfileErrors(data: any): Error[] {
        const errors: ProfileErrors = {};
        const backendErrors: Error[] = data.updateExtensionProfile?.errors ?? [];
        for (const error of backendErrors) {
            const name = error.location[1] as keyof ProfileErrors;
            if (!errors[name]) errors[name] = [];
            errors[name].push(error as unknown as ErrorWithContext);
        }

        this.setProfileErrors(errors);

        return backendErrors;
    }

    private updateValues(updater: (data: Data) => void) {
        const newData: Data = structuredClone(this.values);
        updater(newData);
        this.setValues(newData);
    }

    addNewRuleRow() {
        const data: UserProfileRuleData = {
            sourceNumber: "",
            matchType: UserProfileRuleMatchType.StartsWith,
            action: ExtensionProfileRuleAction.Reject,
            targetNumber: "",
            userProfileId: this.id,
            priority: this.values.rules.length ? Math.max(...this.values.rules.map((r) => r.data.priority)) + 1 : 0,
        };
        this.updateValues((d) => d.rules.push({id: null, data}));
    }

    deleteRule(idx: number) {
        this.updateValues((d) => d.rules.splice(idx, 1));
    }

    updateProfileValues(data: UpdateUserProfileData) {
        this.updateValues((d) => Object.assign(d.profile, data));
    }

    updateRuleValues(idx: number, data: UserProfileRuleData) {
        this.updateValues((d) => Object.assign(d.rules[idx].data, data));
    }

    reorderRules(oldIndex: number, newIndex: number) {
        this.updateValues((d) => {
            const dragRule = d.rules[oldIndex];
            d.rules.splice(oldIndex, 1);
            d.rules.splice(newIndex, 0, dragRule);
            for (const [idx, rule] of d.rules.entries()) {
                rule.data.priority = idx;
            }
        });
    }

    updateActivationSettings(data: UserActivationSettings) {
        const newData = {
            ...this.values,
            ...{profile: {...this.values.profile, settings: {...this.values.profile.settings, ...data}}},
        };
        this.setValues(newData);
    }

    reset() {
        this.setProfileErrors({});
        this.setRuleErrors({});
        this.setValues(this.initialData);
    }

    get hasChanged(): boolean {
        return !deepEqual(this.initialData, this.values);
    }
}
